import { Injectable } from '@angular/core';
import { EventiService } from './eventi.service';
import { EventiDaSincronizzareService } from './eventi-da-sincronizzare.service';
import { TestateInterventiService } from 'src/app/interventi/services/testate-interventi.service';
import { TestateInterventiApertiService } from 'src/app/interventi/services/testate-interventi-da-sincronizzare.service';
import { StatoInterventoAperto, TestataIntervento } from 'src/app/common/models/Intervento';
import { MemorizzatoreRapportiniService } from './memorizzatore-rapportini.service';
import { StatoCompletoEvento } from 'src/app/common/models/Evento';
import { EventiSqlAdapterService } from './eventi-sql-adapter.service';
import { InterventiSqlAdapterService } from 'src/app/interventi/services/interventi-sql-adapter.service';
import { StatoCompletoRicambio } from 'src/app/common/models/Ricambio';
import { RicambiService } from './ricambi.service';
import { RicambiSqlAdapterService } from './ricambi-sql-adapter.service';
import { RicambiDaSincronizzareService } from './ricambi-da-sincronizzare.service';
import { Contatto } from 'src/app/common/models/Contatto';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StatoCompletoSpesa } from 'src/app/common/models/Spesa';
import { SpeseService } from './spese.service';
import { SpeseDaSincronizzareService } from './spese-da-sincronizzare.service';
import { SpeseSqlAdapterService } from './spese-sql-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class InterventoAttivoService {
  statoIntervento: StatoInterventoAperto;

  constructor(
    /**
     * Service in cui e' presenta la logica di sincronizzazione
     * dei singoli eventi di manodopera
     */
    private eventiService: EventiService,
    private eventiOffline: EventiSqlAdapterService,
    private eventiDaSincronizzare: EventiDaSincronizzareService,

    private ricambiService: RicambiService,
    private ricambiOffline: RicambiSqlAdapterService,
    private ricambiDaSincronizzare: RicambiDaSincronizzareService,

    private speseService: SpeseService,
    private speseOffline: SpeseSqlAdapterService,
    private speseDaSincronizzare: SpeseDaSincronizzareService,

    /**
     * Service in cui e' presente la logica di sincronizzazione
     * dei dati su integra
     */
    private testateInterventiOnlineService: TestateInterventiService,
    /**
     * Service di cui si invoca il salvataggio locale delle modifiche
     * non sincronizzate su integra
     */
    private testateInterventiAperti: TestateInterventiApertiService,
    /**
     * Service che fornisce il salvataggio locale delle modifiche
     */
    private testateInterventiOfflineService: InterventiSqlAdapterService,
    /**
     * Service che fornisce il salvataggio su file system, SOLO SU ANDROID,
     * dei file dei rapportini firmati dopo chiusura intervento
     */
    private salvataggioPdfRapportini: MemorizzatoreRapportiniService,
    private loadingCtrl: LoadingController,
    protected translateService: TranslateService,
  ) { }

  async inizializzaInterventoAperto(intervento: TestataIntervento, presenzaDatiDaSincronizzare: boolean) {
    this.statoIntervento = {
      datiTestata: intervento,
      dataModifica: null,
      isTestataDirty: false,
      areEntitaDirties: false,
    };

    if (presenzaDatiDaSincronizzare) {
      // Sostituisco i dati con la copia in Db
      const statoPrecedente = await this.caricaStatInterventoAperto(intervento);
      console.debug(`Caricato lo stato dal db delle modifiche da sincronizzare`);
      this.statoIntervento = statoPrecedente;
    }
  }

  /**
   * Resetta lo stato interno del service per poter
   * aprire in futuro nuovi interventi
   * 
   * Invocato esternamente dalle pagine
   * (I dati sono gia' stati salvati)
   */
  async chiudiInterventoAttivo() {
    this.resetStatoPostChiusura();
  }

  private resetStatoPostChiusura(): void {
    this.statoIntervento = null;
    console.debug('Intervento attivo service pronto per nuovo intervento');
  }

  /**
   * Da storage "offline" ritorna lo stato dell'intervento aperto precedentemente
   */
  private caricaStatInterventoAperto(intervento: TestataIntervento): Promise<StatoInterventoAperto> {
    const statoPrecedente = this.testateInterventiAperti.getInterventoAperto(intervento.idIntervento);
    return statoPrecedente;
  }

  /**
   * Ritorna TRUE se si sta operando su un 
   * intervento
   */
  get isInterventoSelezionato(): boolean {
    return !!this.statoIntervento;
  }

  get isInterventoDirty(): boolean {
    return this.statoIntervento.isTestataDirty || this.statoIntervento.areEntitaDirties;
  }

  /**
   * Ritorna TRUE se si sta operando su un intervento
   * chiudibile (aperto e senza modifiche da sincronizzare)
   */
  get isInterventoChiudibile(): boolean {
    return (!this.isInterventoDirty && this.statoIntervento.datiTestata.codiceStatoIntervento === '20');
  }

  /**
   * Cerca di effettuare la sincronizzazione dei dati di testata dell'intervento.
   * Se fallisce, come effetto collaterale, viene salvata la testate tra nell'apposita
   * cache degli interventi da sincronizzare
   * @param nuoviDatiTestataIntervento
   * @returns TRUE se la sincronizzazione e' riuscita e i dati risultano allineati
   * col server 
   */
  async modificaTestataIntervento(nuoviDatiTestataIntervento: TestataIntervento): Promise<boolean> {
    console.debug('Modificato dati di testata intervento aperto');
    this.statoIntervento.datiTestata = nuoviDatiTestataIntervento;
    const oldDirtyState = this.statoIntervento.isTestataDirty;
    this.statoIntervento.isTestataDirty = true;
    this.statoIntervento.dataModifica = new Date();

    try { // Tentativo di sincronizzazione
      await this.testateInterventiOnlineService.modificaTestataIntervento(this.statoIntervento.datiTestata);
      await this.testateInterventiOfflineService.updateIntervento(this.statoIntervento.datiTestata);
      // Aggiorno anche lo stato del rispettivo record da DB delle testate, se presente
      this.statoIntervento.isTestataDirty = oldDirtyState;
      return true;
    } catch (error) { // Impossibile contattare il server o operazione rifiutata
      this.statoIntervento.isTestataDirty = true;
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Aggiornamento del record di riepilogo (testata + flags vari) sul 
   * Db
   */
  private async aggiornaStatoLocaleIntervento() {
    console.debug('Salvataggio stato intervento aperto');
    await this.testateInterventiAperti.salvaInterventoAperto(this.statoIntervento);
  }

  /**
   * Cerca di aggiungere l'evento di manodopera passato
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà reinserire i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param evento contenitore dell'evento da aggiungere
   * @returns TRUE se l'aggiunta e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async aggiungiEventoManodopera(evento: StatoCompletoEvento): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    // L'evento creato localmente non ha valorizzato alcuni campi
    evento.datiEvento.idIntervento = this.statoIntervento.datiTestata.idIntervento;
    try {
      const isInCreazione = true;
      await this.eventiService.uploadEvento(evento.datiEvento, isInCreazione);
      // Aggiorno anche lo stato del rispettivo record da DB degli eventi, se presente
      await this.eventiOffline.updateEvento(evento.datiEvento);
      await this.eventiDaSincronizzare.rimuoviEventoDaSincronizzare(evento);
 
      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato INSERIMENTO di un evento sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.eventiDaSincronizzare.salvaEventoDaSincronizzare(evento);
      console.debug(`INSERIMENTO nuovo evento salvato su db perche' sincronizzazione fallita`);
      this.statoIntervento.areEntitaDirties = true;
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Cerca di modificare l'evento di manodopera passato
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà rimodificare i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param evento contenitore dell'evento da modificare
   * @returns TRUE se la modifica e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async modificaEventoManodopera(evento: StatoCompletoEvento): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    try {
      const isInCreazione = evento.isPresenteSoloLocalmente;
      await this.eventiService.uploadEvento(evento.datiEvento, isInCreazione);
      await this.eventiOffline.updateEvento(evento.datiEvento);
      /* 
        Se avevo un evento, probabilmente corretto, e in seguito alla modifica
        viene sincronizzato regolarmente devo rimuovere il dato dal Db
        degli eventi da sincronizzare
      */
      await this.rimuoviEventoDaSincronizzare(evento);

      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato MODIFICA di un evento sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.eventiDaSincronizzare.salvaEventoDaSincronizzare(evento);
      this.statoIntervento.areEntitaDirties = true;
      console.debug(`MODIFICA nuovo evento salvato su db perche' sincronizzazione fallita`);
      console.log(`MODIFICA nuovo evento salvato su db perche' sincronizzazione fallita`);
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Cerca di rimuovere l'evento di manodopera passato. 
   * SE l'evento e' stato creato localmente e non esiste online allora
   * l'operazione ha sempre successo altrimenti viene contattato il ws
   * e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce NON viene salvata la rimozione nello storage (l'operatore
   * dovrà ripeterla in futuro)
   * 
   * (Effetti collaterali: Lo stato dirty dell'intervento non viene variato, sia in caso di successo
   * che di fallimento dell'operazione)
   * @param evento contenitore dell'evento da rimuovere
   * @returns TRUE se la rimozione e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async rimuoviEventoManodopera(evento: StatoCompletoEvento): Promise<boolean> {
    if (evento.isPresenteSoloLocalmente) {
      // Oggetto che esiste solamente in app, puo' essere cancellato senza chiamare il server
      await this.eventiDaSincronizzare.rimuoviEventoDaSincronizzare(evento);
      console.debug(`Effettuato RIMOZIONE di un evento solo locale`);
      return true;
    } else { // Tentativo di sincronizzazione col server
      try {
        await this.eventiService.cancellaEvento(evento.datiEvento);
        console.debug(`Effettuato RIMOZIONE di un evento sul server`);
        return true;
      } catch (error) { // Rimozione impossibile o rifiutata
        // NON faccio nulla... rimane ancora da cancellare manualmente dall'operatore
        console.warn(`RIMOZIONE evento sul server fallita`);
        return false;
      }
    }
  }


  private async sincronizzaSingoloEvento(evento: StatoCompletoEvento) {
    const isCreazione = evento.isPresenteSoloLocalmente;
    await this.eventiService.uploadEvento(evento.datiEvento, isCreazione);
    // Caricato su server, lo rimuovo dal DB
    await this.eventiDaSincronizzare.rimuoviEventoDaSincronizzare(evento);
    console.debug(`Evento ${evento.datiEvento.idEvento} di ${evento.datiEvento.idIntervento} sincronizzato e rimosso dalla relativa coda`);
  }

  /**
   * In seguito ad una sincronizzazione va rimosso dal db dei dati disallineati
   * l'evento
   * @param evento 
   */
  private async rimuoviEventoDaSincronizzare(evento: StatoCompletoEvento) {
    const eventiRestanti = await this.eventiDaSincronizzare.rimuoviEventoDaSincronizzare(evento);
    if (!!eventiRestanti || eventiRestanti.length === 0) {
      this.statoIntervento.areEntitaDirties = false;
    }
  }

  /**
   * Cerca di aggiungere l'evento di manodopera passato
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà reinserire i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param evento contenitore dell'evento da aggiungere
   * @returns TRUE se l'aggiunta e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async aggiungiRicambio(ricambio: StatoCompletoRicambio): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    // L'evento creato localmente non ha valorizzato alcuni campi
    ricambio.datiRicambio.idIntervento = this.statoIntervento.datiTestata.idIntervento;
    try {
      const isInCreazione = true;
      await this.ricambiService.uploadRicambio(ricambio.datiRicambio, isInCreazione);
      // Aggiorno anche lo stato del rispettivo record da DB degli eventi, se presente
      await this.ricambiOffline.updateRicambio(ricambio.datiRicambio);

      await this.ricambiDaSincronizzare.rimuoviRicambioDaSincronizzare(ricambio);

      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato INSERIMENTO di un ricambio sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.ricambiDaSincronizzare.salvaRicambioDaSincronizzare(ricambio);
      console.debug(`INSERIMENTO nuovo ricambio salvato su db perche' sincronizzazione fallita`);
      this.statoIntervento.areEntitaDirties = true;
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Cerca di modificare il ricambio passato
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà rimodificare i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param ricambio contenitore del ricambio da modificare
   * @returns TRUE se la modifica e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async modificaRicambio(ricambio: StatoCompletoRicambio): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    try {
      const isInCreazione = false;
      await this.ricambiService.uploadRicambio(ricambio.datiRicambio, isInCreazione);
      await this.ricambiOffline.updateRicambio(ricambio.datiRicambio);
      /* 
        Se avevo un ricambio, probabilmente corretto, e in seguito alla modifica
        viene sincronizzato regolarmente devo rimuovere il dato dal Db
        dai ricambi da sincronizzare
      */
      await this.rimuoviRicambioDaSincronizzare(ricambio);

      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato MODIFICA di un ricambio sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.ricambiDaSincronizzare.salvaRicambioDaSincronizzare(ricambio);
      this.statoIntervento.areEntitaDirties = true;
      console.debug(`MODIFICA nuovo ricambio salvato su db perche' sincronizzazione fallita`);
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Cerca di rimuovere il ricambio passato. 
   * SE l'evento e' stato creato localmente e non esiste online allora
   * l'operazione ha sempre successo altrimenti viene contattato il ws
   * e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce NON viene salvata la rimozione nello storage (l'operatore
   * dovrà ripeterla in futuro)
   * 
   * (Effetti collaterali: Lo stato dirty dell'intervento non viene variato, sia in caso di successo
   * che di fallimento dell'operazione)
   * @param ricambio contenitore del ricambio da rimuovere
   * @returns TRUE se la rimozione e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async rimuoviRicambio(ricambio: StatoCompletoRicambio): Promise<boolean> {
    if (ricambio.isPresenteSoloLocalmente) {
      // Oggetto che esiste solamente in app, puo' essere cancellato senza chiamare il server
      await this.ricambiDaSincronizzare.rimuoviRicambioDaSincronizzare(ricambio);
      console.debug(`Effettuato RIMOZIONE di un ricambio solo locale`);
      return true;
    } else { // Tentativo di sincronizzazione col server
      try {
        await this.ricambiService.cancellaRicambio(ricambio.datiRicambio);
        console.debug(`Effettuato RIMOZIONE di un RICAMBIO sul server`);
        return true;
      } catch (error) { // Rimozione impossibile o rifiutata
        // NON faccio nulla... rimane ancora da cancellare manualmente dall'operatore
        console.warn(`RIMOZIONE RICAMBIO sul server fallita`);
        return false;
      }
    }
  }

  private async sincronizzaSingoloRicambio(ricambio: StatoCompletoRicambio) {
    const isCreazione = ricambio.isPresenteSoloLocalmente;
    await this.ricambiService.uploadRicambio(ricambio.datiRicambio, isCreazione);
    // Caricato su server, lo rimuovo dal DB
    await this.ricambiDaSincronizzare.rimuoviRicambioDaSincronizzare(ricambio);
    console.debug(`Ricambio ${ricambio.datiRicambio.idProdotto} di ${ricambio.datiRicambio.idIntervento} sincronizzato e rimosso dalla relativa coda`);
  }

  /**
   * In seguito ad una sincronizzazione va rimosso dal db dei dati disallineati
   * l'evento
   * @param evento 
   */
  private async rimuoviRicambioDaSincronizzare(ricambio: StatoCompletoRicambio) {
    const ricambiRestanti = await this.ricambiDaSincronizzare.rimuoviRicambioDaSincronizzare(ricambio);
    if (!!ricambiRestanti || ricambiRestanti.length === 0) {
      this.statoIntervento.areEntitaDirties = false;
    }
  }

  /**
   * Cerca di aggiungere l'evento di manodopera passato
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà reinserire i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param evento contenitore dell'evento da aggiungere
   * @returns TRUE se l'aggiunta e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async aggiungiSpesa(spesa: StatoCompletoSpesa): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    // L'evento creato localmente non ha valorizzato alcuni campi
    spesa.datiSpesa.idIntervento = this.statoIntervento.datiTestata.idIntervento;
    try {
      const isInCreazione = true;
      await this.speseService.uploadSpesa(spesa.datiSpesa, isInCreazione);
      // Aggiorno anche lo stato del rispettivo record da DB degli eventi, se presente
      await this.speseOffline.updateSpesa(spesa.datiSpesa);

      await this.speseDaSincronizzare.rimuoviSpesaDaSincronizzare(spesa);

      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato INSERIMENTO di una spesa sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.speseDaSincronizzare.salvaSpesaDaSincronizzare(spesa);
      console.debug(`INSERIMENTO nuova spesa salvato su db perche' sincronizzazione fallita`);
      this.statoIntervento.areEntitaDirties = true;
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

  /**
   * Cerca di modificare la spesa passata
   * con una sincronizzazione via ws e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce i dati vengono salvati localmente nello storage (l'operatore
   * NON dovrà rimodificare i dati pero' questi dovranno essere passati al server
   * prima di poter fare la chiusura dell'intervento)
   * @param spesa contenitore della spesa da modificare
   * @returns TRUE se la modifica e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
  async modificaSpesa(spesa: StatoCompletoSpesa): Promise<boolean> {
    const oldDirtyState = this.statoIntervento.areEntitaDirties;
    this.statoIntervento.areEntitaDirties = true;
    this.statoIntervento.dataModifica = new Date();

    try {
      const isInCreazione = false;
      await this.speseService.uploadSpesa(spesa.datiSpesa, isInCreazione);
      await this.speseOffline.updateSpesa(spesa.datiSpesa);
      /* 
        Se avevo un ricambio, probabilmente corretto, e in seguito alla modifica
        viene sincronizzato regolarmente devo rimuovere il dato dal Db
        dai ricambi da sincronizzare
      */
      await this.rimuoviSpesaDaSincronizzare(spesa);

      oldDirtyState ? await this.setEntitiesDirty() : this.statoIntervento.areEntitaDirties = oldDirtyState;
      console.debug(`Effettuato MODIFICA di un ricambio sul server`);
      return true;
    } catch (error) { // Aggiunta impossibile o rifiutata
      await this.speseDaSincronizzare.salvaSpesaDaSincronizzare(spesa);
      this.statoIntervento.areEntitaDirties = true;
      console.debug(`MODIFICA nuova spesa salvato su db perche' sincronizzazione fallita`);
      return false;
    } finally {
      await this.aggiornaStatoLocaleIntervento();
    }
  }

   /**
   * Cerca di rimuovere il ricambio passato. 
   * SE l'evento e' stato creato localmente e non esiste online allora
   * l'operazione ha sempre successo altrimenti viene contattato il ws
   * e: 
   * - Se va a buon fine viene ritornato true
   * - Se fallisce NON viene salvata la rimozione nello storage (l'operatore
   * dovrà ripeterla in futuro)
   * 
   * (Effetti collaterali: Lo stato dirty dell'intervento non viene variato, sia in caso di successo
   * che di fallimento dell'operazione)
   * @param ricambio contenitore del ricambio da rimuovere
   * @returns TRUE se la rimozione e' stata eseguita (ed eventualmente 
   * sincronizzata col ws)
   */
   async rimuoviSpesa(spesa: StatoCompletoSpesa): Promise<boolean> {
    if (spesa.isPresenteSoloLocalmente) {
      // Oggetto che esiste solamente in app, puo' essere cancellato senza chiamare il server
      await this.speseDaSincronizzare.rimuoviSpesaDaSincronizzare(spesa);
      console.debug(`Effettuato RIMOZIONE di una spesa solo locale`);
      return true;
    } else { // Tentativo di sincronizzazione col server
      try {
        await this.speseService.cancellaSpesa(spesa.datiSpesa);
        console.debug(`Effettuato RIMOZIONE di una SPESA sul server`);
        return true;
      } catch (error) { // Rimozione impossibile o rifiutata
        // NON faccio nulla... rimane ancora da cancellare manualmente dall'operatore
        console.warn(`RIMOZIONE SPESA sul server fallita`);
        return false;
      }
    }
  }

  private async sincronizzaSingolaSpesa(spesa: StatoCompletoSpesa) {
    const isCreazione = spesa.isPresenteSoloLocalmente;
    await this.speseService.uploadSpesa(spesa.datiSpesa, isCreazione);
    // Caricato su server, lo rimuovo dal DB
    await this.speseDaSincronizzare.rimuoviSpesaDaSincronizzare(spesa);
    console.debug(`Spesa ${spesa.datiSpesa.idProdotto} di ${spesa.datiSpesa.idIntervento} sincronizzato e rimosso dalla relativa coda`);
  }

  /**
   * In seguito ad una sincronizzazione va rimosso dal db dei dati disallineati
   * la spesa
   * @param spesa 
   */
  private async rimuoviSpesaDaSincronizzare(spesa: StatoCompletoSpesa) {
    const speseRestanti = await this.speseDaSincronizzare.rimuoviSpesaDaSincronizzare(spesa);
    if (!!speseRestanti || speseRestanti.length === 0) {
      this.statoIntervento.areEntitaDirties = false;
    }
  }

  /**
   * Chiusura dell'intervento che porta all'acquisizione dei dati
   * del referente del cliente (nominativo, note e firma) per la
   * generazione del rapportino
   * @param nomeCliente
   * @param noteIntervento
   * @param codiceAddetto chi sta chiudendo l'intervento
   * @param noteTecnico
   * @param firmaBlobBase64 
   * @param destinatariMail elenco dei destinatari della mail con il rapportino
   */
  async chiudiIntervento(
    nomeCliente: string, noteIntervento: string, codiceAddetto: string, noteTecnico: string,
    firmaBlobBase64: string, destinatariMail: Contatto[]): Promise<string> {
    // PRIMA DI PROCEDERE NON CI DEVONO ESSERE MODIFICHE NON SINCRONIZZATE
    if (!this.isInterventoChiudibile) {
      throw new Error('Intervento non chiudibile');
    }
    console.debug('Tentativo di chiusura intervento attivo');
    const{...testataInv} = this.statoIntervento.datiTestata

    testataInv.noteIntervento = noteIntervento;
    testataInv.noteDiAttivita = noteTecnico;
    testataInv.firmatarioCliente = nomeCliente;
    const response = await this.testateInterventiOnlineService.patchTestataIntervento(testataInv)
    
    this.statoIntervento.datiTestata.noteIntervento = noteIntervento;
    this.statoIntervento.datiTestata.noteDiAttivita = noteTecnico;
    this.statoIntervento.datiTestata.firmatarioCliente = nomeCliente;
    this.statoIntervento.datiTestata.firmaChisuraClienteBlob = firmaBlobBase64;
    this.statoIntervento.datiTestata.interessatiAMail = destinatariMail;
    try {
      const pdfFirmato = await this.testateInterventiOnlineService.chiudiIntervento(this.statoIntervento.datiTestata, codiceAddetto);
      console.debug('Intervento chiuso');

      await this.salvataggioPdfRapportini.saveSignedReport(this.statoIntervento.datiTestata, pdfFirmato);

      // TODO: chiudere l'intervento aperto globalmente
      return pdfFirmato;
    } catch (error) {
      console.error(`Impossibile chiudere l'intervento`, error);
      throw error;
    }
  }

  async sincronizzaIntervento() {
    const loading = await this.mostraLoading();
    // Sincronizza eventuali dati di testata disallineati
    const { idIntervento } = this.statoIntervento.datiTestata;
    const ultimiDatiTestata = await this.testateInterventiAperti.getInterventoAperto(idIntervento);
    try {
      await this.testateInterventiOnlineService.modificaTestataIntervento(ultimiDatiTestata.datiTestata);
      this.statoIntervento.isTestataDirty = false;
      console.debug('Testata sincronizzata');

      const eventiDaSincronizzare = await this.eventiDaSincronizzare.getEventiDaSincronizzare(idIntervento);
      if (eventiDaSincronizzare && eventiDaSincronizzare.length > 0) {
        const operazioniSincronizzazioneEventi: Promise<any>[] = [];
        eventiDaSincronizzare.forEach(evento => operazioniSincronizzazioneEventi.push(this.sincronizzaSingoloEvento(evento)));
        await Promise.all(operazioniSincronizzazioneEventi);
        console.debug(`Eventi sincronizzati`);
      }

      const ricambiDaSincronizzare = await this.ricambiDaSincronizzare.getRicambiDaSincronizzare(idIntervento);
      if (ricambiDaSincronizzare && ricambiDaSincronizzare.length > 0) {
        const operazioniSincronizzazioneRicambi: Promise<any>[] = [];
        ricambiDaSincronizzare.forEach(ricambio => operazioniSincronizzazioneRicambi.push(this.sincronizzaSingoloRicambio(ricambio)));
        await Promise.all(operazioniSincronizzazioneRicambi);
        console.debug(`Ricambi sincronizzati`);
      }

      const speseDaSincronizzare = await this.speseDaSincronizzare.getSpeseDaSincronizzare(idIntervento);
      if (speseDaSincronizzare && speseDaSincronizzare.length > 0) {
        const operazioniSincronizzazioneSpese: Promise<any>[] = [];
        speseDaSincronizzare.forEach(spesa => operazioniSincronizzazioneSpese.push(this.sincronizzaSingolaSpesa(spesa)));
        await Promise.all(operazioniSincronizzazioneSpese);
        console.debug(`Spese sincronizzate`);
      }
      this.statoIntervento.areEntitaDirties = false;
    } catch (error) {
      console.error(`La sincronizzazione della testata o di un'entità dell'intervento (evento, ricambio o spesa) e' fallita`);
      throw error;
    } finally {
      await loading.dismiss();
      this.aggiornaStatoLocaleIntervento();
    }
  }

  private async mostraLoading(): Promise<HTMLIonLoadingElement> {
    const loading = await this.loadingCtrl.create({
      message: this.translateService.instant('CommonWords.syncInProgress'),
      backdropDismiss: false,
    });
    loading.present();
    return loading;
  }

  /**
   * Ripristina lo stato dell'intervento cancellando tutte le modifiche
   * LOCALI alla testata e agli eventi collegati presenti nel db
   */
  async dimenticaModificheDaSincronizzare() {
    // cancello i dati di testata
    await this.testateInterventiAperti.rimuoviIntervento(this.statoIntervento);
    const { idIntervento } = this.statoIntervento.datiTestata;
    await this.eventiDaSincronizzare.rimuoviEventiDaSincronizzare(idIntervento);
    await this.ricambiDaSincronizzare.rimuoviRicambiDaSincronizzare(idIntervento);
    console.debug(`Cancellate tutte le modifiche locali dell'intervento ${idIntervento}`);
  }

  async areEntitiesDirty(): Promise<boolean>{
    const { idIntervento } = this.statoIntervento.datiTestata;
    const eventiDaSincronizzare = await this.eventiDaSincronizzare.getEventiDaSincronizzare(idIntervento);
    const ricambiDaSincronizzare = await this.ricambiDaSincronizzare.getRicambiDaSincronizzare(idIntervento);
    return eventiDaSincronizzare.length > 0 || ricambiDaSincronizzare.length > 0 ? true : false
  }

  async setEntitiesDirty(){
    this.statoIntervento.areEntitaDirties = await this.areEntitiesDirty();
  }
}